import { useTranslation } from 'react-i18next';

import { cn } from '../utils/cn';

export interface StepperItem {
  id: string;
  label: string;
}

interface StepperProps {
  steps: StepperItem[];
  /** Index of the currently active step. */
  activeIndex: number;
  /** Indices of steps already completed (rendered with a check). */
  completedIndices?: number[];
  /**
   * Optional handler when a previous step is tapped. Forward navigation is
   * intentionally not supported — the user advances by completing the
   * current step's form, not by tapping ahead.
   */
  onJump?: (index: number) => void;
}

/**
 * Horizontal numbered mini-stepper used by Fase B. The decision to render
 * numbered dots (instead of a progress bar or chip breadcrumb) is motivated
 * by three properties:
 *
 *   1. Contextual: the user always sees where they are AND how many steps
 *      remain — fuels the Zeigarnik effect that nudges completion.
 *   2. Tappable: previous dots are interactive so the user can jump back to
 *      fix data without losing the rest of the wizard state.
 *   3. Scales to mobile: dots + thin connectors collapse cleanly even on
 *      narrow viewports; a breadcrumb of full labels wouldn't.
 */
export const Stepper = ({
  steps,
  activeIndex,
  completedIndices = [],
  onJump,
}: StepperProps) => {
  const { t } = useTranslation('smart');
  const isComplete = (i: number) =>
    completedIndices.includes(i) || i < activeIndex;

  const lastIndex = steps.length - 1;

  return (
    <nav aria-label={t('wizard.stepper.ariaLabel')} className="w-full">
      <ol className="flex w-full items-center gap-2">
        {steps.map((step, i) => {
          const active = i === activeIndex;
          const done = isComplete(i);
          const canJump = onJump && i < activeIndex;
          const isLast = i === lastIndex;
          return (
            // Only intermediate items grow (`flex-1`) so their connector fills
            // the remaining space evenly. The last item shrinks to the button
            // width so the final dot lands flush with the right edge — without
            // this, every step count (2/3/4/…) would leave dead space on the
            // right and visually decenter the row.
            <li
              key={step.id}
              className={cn(
                'flex items-center gap-2',
                !isLast && 'flex-1',
              )}
            >
              <button
                type="button"
                disabled={!canJump}
                onClick={canJump ? () => onJump?.(i) : undefined}
                aria-current={active ? 'step' : undefined}
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-colors',
                  'focus-visible:ring-accent focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg focus-visible:outline-none',
                  done
                    ? 'bg-accent text-accent-fg'
                    : active
                      ? 'bg-fg text-bg'
                      : 'bg-bg-muted text-fg-muted',
                  canJump && 'cursor-pointer hover:brightness-110',
                )}
                aria-label={`${step.label}${active ? ` ${t('wizard.stepper.ariaCurrent')}` : ''}`}
              >
                {done ? '✓' : i + 1}
              </button>
              {!isLast && (
                <span
                  aria-hidden
                  className={cn(
                    'h-px flex-1 transition-colors',
                    isComplete(i + 1) || done ? 'bg-accent' : 'bg-border',
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
      <p
        className="text-fg-muted mt-2 text-center text-[11px] font-medium uppercase tracking-wide"
        aria-live="polite"
      >
        {steps[activeIndex]?.label ?? ''}
      </p>
    </nav>
  );
};
